import { SearchSharp } from "@mui/icons-material";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  InputAdornment,
  MenuItem,
  TextField,
} from "@mui/material";

import type { OrderStatus } from "../../types/order";

export const STATUS_CHIP_CONFIG: Record<
  string,
  { label: string; bg: string; color: string }
> = {
  RECEIVED: { label: "Order Received", bg: "#D4EDFF", color: "#0054FC" },
  CREATED: { label: "Order Created", bg: "#E3F5E3", color: "#2D6830" },
  REQUEST_MORE_INFO: {
    label: "Request More Details",
    bg: "#FFFBC5",
    color: "#BB6902",
  },
  UPDATED: { label: "Updated", bg: "#ECE9FE", color: "#662BD6" },
  CANCELLED: { label: "Cancelled", bg: "#FFE3E1", color: "#BE2217" },
};

type StatusOption = {
  label: string;
  value: OrderStatus;
  bg: string;
  color: string;
};

const STATUS_OPTIONS: StatusOption[] = [
  { value: "RECEIVED", label: "Order Received", bg: "#D4EDFF", color: "#0054FC" },
  { value: "CREATED", label: "Order Created", bg: "#E3F5E3", color: "#2D6830" },
  { value: "REQUEST_MORE_INFO", label: "Request More Details", bg: "#FFFBC5", color: "#BB6902" },
  { value: "UPDATED", label: "Updated", bg: "#ECE9FE", color: "#662BD6" },
  { value: "CANCELLED", label: "Cancelled", bg: "#FFE3E1", color: "#BE2217" },
];

export const ALL_STATUSES: OrderStatus[] = STATUS_OPTIONS.map((o) => o.value);

export const DEFAULT_STATUSES: OrderStatus[] = [
  "RECEIVED",
  "REQUEST_MORE_INFO",
  "UPDATED",
  "CANCELLED",
];

export const MONTH_OPTIONS = [
  { label: "All Month", value: "all" },
  { label: "January", value: "01" },
  { label: "February", value: "02" },
  { label: "March", value: "03" },
  { label: "April", value: "04" },
  { label: "May", value: "05" },
  { label: "June", value: "06" },
  { label: "July", value: "07" },
  { label: "August", value: "08" },
  { label: "September", value: "09" },
  { label: "October", value: "10" },
  { label: "November", value: "11" },
  { label: "December", value: "12" },
];

export type OrderListFilters = {
  search: string;
  statuses: OrderStatus[];
  month: string;
  year: string;
  page: number;
  page_size: number;
};

export interface FilterControlsProps {
  filters: OrderListFilters;
  yearOptions: Array<{ label: string; value: string }>;
  onSearchChange: (value: string) => void;
  onMonthChange: (value: string) => void;
  onYearChange: (value: string) => void;
  onStatusesChange: (value: OrderStatus[]) => void;
}

export const FilterControls = ({
  filters,
  yearOptions,
  onSearchChange,
  onMonthChange,
  onYearChange,
  onStatusesChange,
}: FilterControlsProps) => {
  const selectedOptions = STATUS_OPTIONS.filter((o) =>
    filters.statuses.includes(o.value),
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: 1.5,
        alignItems: "center",
      }}
    >
      <TextField
        size="small"
        placeholder="Search"
        value={filters.search}
        onChange={(e) => onSearchChange(e.target.value)}
        sx={{ minWidth: 120, maxWidth: 180 }}
        slotProps={{
          input: {
            endAdornment: (
              <InputAdornment position="end">
                <SearchSharp sx={{ color: "text.secondary", fontSize: 20 }} />
              </InputAdornment>
            ),
          },
        }}
      />
      <TextField
        select
        size="small"
        value={filters.month}
        onChange={(e) => onMonthChange(e.target.value)}
        sx={{
          minWidth: 100,
          maxWidth: 180,
          "& .MuiSelect-select": {
            color:
              filters.month === "all" ? "text.secondary" : "text.primary",
          },
        }}
      >
        {MONTH_OPTIONS.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        select
        size="small"
        value={filters.year}
        onChange={(e) => onYearChange(e.target.value)}
        sx={{ minWidth: 80, maxWidth: 120 }}
      >
        {yearOptions.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>
      <Autocomplete
        multiple
        disableCloseOnSelect
        disableClearable
        options={STATUS_OPTIONS}
        value={selectedOptions}
        onChange={(_, newValue) => {
          const next = newValue.map((o) => o.value);
          onStatusesChange(next.length > 0 ? next : DEFAULT_STATUSES);
        }}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        renderOption={(props, option, { selected }) => {
          const { key, ...optionProps } = props;
          return (
            <li
              key={key}
              {...optionProps}
              style={{ ...optionProps.style, padding: 0 }}
            >
              <Box
                component="span"
                onClick={(e) => {
                  e.stopPropagation();
                  // Checkbox area: toggle this status
                  const current = filters.statuses;
                  const next = selected
                    ? current.filter((s) => s !== option.value)
                    : [...current, option.value];
                  onStatusesChange(next.length > 0 ? next : DEFAULT_STATUSES);
                }}
                sx={{ display: "flex", alignItems: "center", pl: 2, py: 1, cursor: "pointer" }}
              >
                <Checkbox
                  icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                  checkedIcon={<CheckBoxIcon fontSize="small" />}
                  sx={{ p: 0 }}
                  checked={selected}
                />
              </Box>
              <Box
                component="span"
                onClick={(e) => {
                  e.stopPropagation();
                  // Chip area: solo — select only this status
                  onStatusesChange([option.value]);
                }}
                sx={{ display: "flex", alignItems: "center", flex: 1, py: 1, pr: 2, pl: 1, cursor: "pointer" }}
              >
                <Chip
                  label={option.label}
                  size="small"
                  sx={{
                    height: 24,
                    borderRadius: 1,
                    backgroundColor: option.bg,
                    color: option.color,
                    pointerEvents: "none",
                    "& .MuiChip-label": {
                      px: 1,
                      fontSize: 12,
                      fontWeight: 400,
                      lineHeight: 1.4,
                    },
                  }}
                />
              </Box>
            </li>
          );
        }}
        renderTags={(value) => {
          const first = value[0];
          const hidden = value.length - 1;

          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, overflow: "hidden", ml: 0.5 }}>
              <Chip
                label={first.label}
                size="small"
                sx={{
                  height: 24,
                  borderRadius: 1,
                  backgroundColor: first.bg,
                  color: first.color,
                  "& .MuiChip-label": {
                    px: 1,
                    fontSize: 12,
                    fontWeight: 400,
                    lineHeight: 1.4,
                  },
                }}
              />
              {hidden > 0 && (
                <Chip
                  label={`+${hidden}`}
                  size="small"
                  sx={{
                    height: 24,
                    borderRadius: 1,
                    backgroundColor: "#F0F0F0",
                    color: "#555",
                    flexShrink: 0,
                    "& .MuiChip-label": {
                      px: 1,
                      fontSize: 12,
                      fontWeight: 600,
                    },
                  }}
                />
              )}
            </Box>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            placeholder={selectedOptions.length === 0 ? "All State Order" : ""}
            sx={{
              "& .MuiInputBase-input": {
                minWidth: selectedOptions.length > 0 ? "0 !important" : undefined,
                p: selectedOptions.length > 0 ? "2px 0 !important" : undefined,
              },
            }}
          />
        )}
        sx={{
          width: 220,
          "& .MuiAutocomplete-inputRoot": {
            flexWrap: "nowrap",
            height: 40,
            py: "0 !important",
            alignItems: "center",
          },
        }}
      />
    </Box>
  );
};
